/**
 * Admin → Settings → Do-Not-Source tab — feature E07-F07 (E07-F07-T04).
 *
 * ONE shared list of EER's client companies; every new research run excludes
 * them automatically (spec docs/superpowers/specs/2026-10-14-global-dns-eer-clients-design.md).
 *
 *   - table: name, domain, source badge (Manual / From project), added date;
 *     search; active counter; Active / Removed filter;
 *   - row actions: Edit (name/domain dialog), Remove (soft), Restore (Removed filter);
 *   - add flow: paste → Preview (New / Already in list / Unrecognized) → «Add N»;
 *   - «Pull clients from projects» → preview (Will be added / Already in list /
 *     Removed — stays removed) → Cancel or «Add N» (E07-F07-T06); the notice
 *     quotes the live Active / Removed tab counters.
 *
 * Read-only for non-admins (`canEdit=false`): the table stays visible, every
 * write control is gone. Writes are admin-only server-side too.
 *
 * Transport: dnsClientsApi.ts (mock adapter at prototype stage).
 */
import { useMemo, useState } from 'react';
import { Dialog, DialogFooter, SegmentedToggle, useToast } from '../ui/index.js';
import { errorMessage } from '../api/client.js';
import {
  useAddDnsClients,
  useDnsClients,
  usePreviewDnsPaste,
  usePreviewDnsPull,
  usePullDnsFromProjects,
  useRemoveDnsClient,
  useRestoreDnsClient,
  useUpdateDnsClient,
  type DnsClientEntry,
  type DnsPastePreview,
  type DnsPullPreview,
  type DnsPullResult,
} from './dnsClientsApi.js';
import { filterDnsEntries } from './dnsClientsModel.js';

const FIELD_CLASS =
  'w-full rounded border border-border px-3 py-2 text-sm focus:border-primary focus:outline-none';

const PASTE_PLACEHOLDER = [
  'One company per line:',
  'Marvell Technology',
  'latticesemi.com',
  'Silicon Labs\tsilabs.com      ← two columns from Excel',
  'Astera Labs, asteralabs.com',
].join('\n');

const DATE_FMT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : DATE_FMT.format(d);
}

type Filter = 'active' | 'removed';

// ---------------------------------------------------------------------------
// Source badge
// ---------------------------------------------------------------------------

function SourceBadge({ entry }: { readonly entry: DnsClientEntry }): React.JSX.Element {
  if (entry.source === 'project_client') {
    return (
      <span
        data-testid="dns-source-badge"
        data-source="project_client"
        className="inline-flex items-center gap-1 rounded-full bg-accent/15 px-2 py-0.5 text-xs font-medium text-accent-hover"
        title={entry.clientId !== null ? `Client #${entry.clientId}` : undefined}
      >
        <i className="fa-solid fa-folder-open text-[10px]" aria-hidden /> From project
      </span>
    );
  }
  return (
    <span
      data-testid="dns-source-badge"
      data-source="manual"
      className="inline-flex items-center gap-1 rounded-full bg-surface-muted px-2 py-0.5 text-xs font-medium text-text-muted ring-1 ring-border"
    >
      <i className="fa-solid fa-keyboard text-[10px]" aria-hidden /> Manual
    </span>
  );
}

// ---------------------------------------------------------------------------
// Edit dialog
// ---------------------------------------------------------------------------

function EditDialog({
  entry,
  onClose,
}: {
  readonly entry: DnsClientEntry;
  readonly onClose: () => void;
}): React.JSX.Element {
  const toast = useToast();
  const updateMut = useUpdateDnsClient();
  const [name, setName] = useState(entry.name);
  const [domain, setDomain] = useState(entry.domain ?? '');
  const [error, setError] = useState<string | null>(null);

  function handleSave(): void {
    setError(null);
    updateMut.mutate(
      { id: entry.id, body: { name, domain } },
      {
        onSuccess: () => {
          toast.success('Company updated');
          onClose();
        },
        onError: (e) => setError(errorMessage(e, 'Save error')),
      },
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title="Edit company"
      maxWidth="md"
      testId="dns-edit-dialog"
      footer={
        <DialogFooter
          onCancel={onClose}
          onConfirm={handleSave}
          confirmLabel="Save"
          confirmDisabled={updateMut.isPending}
          testId="dns-edit-footer"
        />
      }
    >
      <div className="grid gap-4">
        <label htmlFor="dns-edit-name" className="block">
          <span className="mb-1 block text-xs font-medium text-text-strong">Name</span>
          <input
            id="dns-edit-name"
            data-testid="dns-edit-name"
            type="text"
            className={FIELD_CLASS}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label htmlFor="dns-edit-domain" className="block">
          <span className="mb-1 block text-xs font-medium text-text-strong">Domain</span>
          <input
            id="dns-edit-domain"
            data-testid="dns-edit-domain"
            type="text"
            className={FIELD_CLASS}
            value={domain}
            placeholder="company.com — leave empty to match by name"
            onChange={(e) => setDomain(e.target.value)}
          />
          <span className="mt-1 block text-xs text-text-muted">
            With a domain, research matches the company by its website — more reliable than the
            name.
          </span>
        </label>
        {error ? (
          <p
            data-testid="dns-edit-error"
            className="rounded bg-danger/10 px-3 py-2 text-xs text-danger"
          >
            <i className="fa-solid fa-circle-exclamation mr-1" aria-hidden />
            {error}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Paste panel (add flow)
// ---------------------------------------------------------------------------

function PreviewGroup({
  tone,
  title,
  count,
  testId,
  children,
}: {
  readonly tone: 'new' | 'existing' | 'bad';
  readonly title: string;
  readonly count: number;
  readonly testId: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  const toneClass = {
    new: 'border-success/40 bg-success/5',
    existing: 'border-border bg-surface-muted',
    bad: 'border-warning/50 bg-warning/5',
  }[tone];
  const dotClass = { new: 'bg-success', existing: 'bg-text-muted', bad: 'bg-warning' }[tone];
  return (
    <section
      data-testid={testId}
      className={`flex min-w-0 flex-col rounded-lg border ${toneClass}`}
    >
      <header className="flex items-center gap-2 border-b border-inherit px-3 py-2">
        <span className={`h-2 w-2 rounded-full ${dotClass}`} aria-hidden />
        <span className="text-xs font-semibold uppercase tracking-wide text-text-strong">
          {title}
        </span>
        <span
          data-testid={`${testId}-count`}
          className="ml-auto text-sm font-semibold tabular-nums text-text-strong"
        >
          {count}
        </span>
      </header>
      <ul className="max-h-56 overflow-y-auto px-3 py-2 text-xs">
        {count === 0 ? <li className="italic text-text-muted">None</li> : children}
      </ul>
    </section>
  );
}

function PastePanel({ onClose }: { readonly onClose: () => void }): React.JSX.Element {
  const toast = useToast();
  const previewMut = usePreviewDnsPaste();
  const addMut = useAddDnsClients();
  const [text, setText] = useState('');
  const [preview, setPreview] = useState<DnsPastePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const lineCount = text.split(/\r?\n/).filter((l) => l.trim() !== '').length;

  function handleText(value: string): void {
    setText(value);
    // The preview describes the text it was built from — any edit makes it stale.
    setPreview(null);
    setError(null);
  }

  function handlePreview(): void {
    setError(null);
    previewMut.mutate(text, {
      onSuccess: (p) => setPreview(p),
      onError: (e) => setError(errorMessage(e, 'Preview failed')),
    });
  }

  function handleAdd(): void {
    addMut.mutate(text, {
      onSuccess: ({ added }) => {
        toast.success(`Added ${added} ${added === 1 ? 'company' : 'companies'} to Do-Not-Source`);
        onClose();
      },
      onError: (e) => setError(errorMessage(e, 'Save error')),
    });
  }

  const freshCount = preview?.fresh.length ?? 0;

  return (
    <div
      data-testid="dns-paste-panel"
      className="mb-5 rounded-lg border border-border bg-white p-4 shadow-[0_1px_0_var(--color-border),0_8px_24px_-16px_rgba(15,23,42,0.35)]"
    >
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-medium text-text-strong">
          <i className="fa-solid fa-paste mr-2 text-accent" aria-hidden />
          Paste a list
        </h3>
        <button
          type="button"
          data-testid="dns-paste-close"
          onClick={onClose}
          className="text-text-muted hover:text-text-strong"
          aria-label="Close paste panel"
        >
          <i className="fa-solid fa-xmark" />
        </button>
      </div>

      {preview === null ? (
        <>
          <textarea
            data-testid="dns-paste-input"
            aria-label="Companies, one per line"
            className={`${FIELD_CLASS} h-44 resize-y font-mono text-xs leading-5`}
            value={text}
            placeholder={PASTE_PLACEHOLDER}
            onChange={(e) => handleText(e.target.value)}
          />
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <span className="text-xs text-text-muted">
              Name · domain or URL · Name⇥domain (two Excel columns) · Name, domain
            </span>
            <span
              data-testid="dns-paste-lines"
              className="ml-auto text-xs tabular-nums text-text-muted"
            >
              {lineCount} {lineCount === 1 ? 'line' : 'lines'}
            </span>
            <button
              type="button"
              data-testid="dns-paste-preview"
              disabled={lineCount === 0 || previewMut.isPending}
              onClick={handlePreview}
              className="rounded bg-primary px-3 py-2 text-sm text-surface hover:bg-primary-hover disabled:opacity-50"
            >
              {previewMut.isPending ? (
                <i className="fa-solid fa-spinner fa-spin mr-1" aria-hidden />
              ) : (
                <i className="fa-solid fa-eye mr-1" aria-hidden />
              )}
              Preview
            </button>
          </div>
        </>
      ) : (
        <>
          <div data-testid="dns-preview" className="grid gap-3 md:grid-cols-3">
            <PreviewGroup
              tone="new"
              title="New"
              count={preview.fresh.length}
              testId="dns-preview-new"
            >
              {preview.fresh.map((it) => (
                <li key={it.line} data-testid="dns-preview-new-item" className="flex gap-2 py-0.5">
                  <span className="truncate font-medium text-text-strong">{it.name}</span>
                  {it.domain && it.domain !== it.name ? (
                    <span className="truncate text-text-muted">{it.domain}</span>
                  ) : null}
                  {!it.domain ? <span className="text-text-muted italic">by name</span> : null}
                </li>
              ))}
            </PreviewGroup>
            <PreviewGroup
              tone="existing"
              title="Already in list"
              count={preview.existing.length}
              testId="dns-preview-existing"
            >
              {preview.existing.map((it) => (
                <li key={it.line} data-testid="dns-preview-existing-item" className="py-0.5">
                  <span className="font-medium text-text-strong">{it.name}</span>{' '}
                  <span className="text-text-muted">
                    {it.match === null
                      ? '— repeated in this paste'
                      : it.match.isActive
                        ? `= ${it.match.name}`
                        : `= ${it.match.name} (removed — restore it under Removed)`}
                  </span>
                </li>
              ))}
            </PreviewGroup>
            <PreviewGroup
              tone="bad"
              title="Unrecognized"
              count={preview.unrecognized.length}
              testId="dns-preview-bad"
            >
              {preview.unrecognized.map((it) => (
                <li key={it.line} data-testid="dns-preview-bad-item" className="py-0.5">
                  <span className="mr-1 tabular-nums text-text-muted">L{it.line}</span>
                  <code className="rounded bg-white px-1 text-text-strong">{it.raw.trim()}</code>
                  <span className="block text-warning">{it.reason}</span>
                </li>
              ))}
            </PreviewGroup>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <button
              type="button"
              data-testid="dns-preview-back"
              onClick={() => setPreview(null)}
              className="text-sm text-text-muted hover:text-text-strong"
            >
              <i className="fa-solid fa-arrow-left mr-1" aria-hidden /> Edit list
            </button>
            {preview.unrecognized.length > 0 ? (
              <span className="text-xs text-text-muted">Unrecognized lines are skipped.</span>
            ) : null}
            <button
              type="button"
              data-testid="dns-add-confirm"
              disabled={freshCount === 0 || addMut.isPending}
              onClick={handleAdd}
              className="ml-auto rounded bg-primary px-4 py-2 text-sm font-medium text-surface hover:bg-primary-hover disabled:opacity-50"
            >
              {addMut.isPending ? (
                <i className="fa-solid fa-spinner fa-spin mr-1" aria-hidden />
              ) : null}
              Add {freshCount}
            </button>
          </div>
        </>
      )}

      {error ? (
        <p
          data-testid="dns-paste-error"
          className="mt-3 rounded bg-danger/10 px-3 py-2 text-xs text-danger"
        >
          <i className="fa-solid fa-circle-exclamation mr-1" aria-hidden />
          {error}
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pull preview (E07-F07-T06): nothing is added until the admin confirms
// ---------------------------------------------------------------------------

function PullPanel({
  preview,
  onClose,
  onDone,
}: {
  readonly preview: DnsPullPreview;
  readonly onClose: () => void;
  readonly onDone: (result: DnsPullResult) => void;
}): React.JSX.Element {
  const toast = useToast();
  const pullMut = usePullDnsFromProjects();
  const [error, setError] = useState<string | null>(null);
  const freshCount = preview.fresh.length;

  function handleConfirm(): void {
    setError(null);
    pullMut.mutate(
      preview.fresh.map((c) => c.clientId),
      {
        onSuccess: (r) => {
          // The preview's skipped groups are distinct entries — the same rows the
          // Active / Removed tabs count, so the notice never exceeds the tabs.
          const result: DnsPullResult = {
            added: r.added,
            alreadyListed: preview.alreadyListed.length + r.alreadyListed,
            skippedRemoved: preview.removed.length + r.skippedRemoved,
          };
          toast.success(`Added ${r.added} ${r.added === 1 ? 'client' : 'clients'} from projects`);
          onDone(result);
        },
        onError: (e) => setError(errorMessage(e, 'Pull failed')),
      },
    );
  }

  return (
    <div
      data-testid="dns-pull-panel"
      className="mb-5 rounded-lg border border-border bg-white p-4 shadow-[0_1px_0_var(--color-border),0_8px_24px_-16px_rgba(15,23,42,0.35)]"
    >
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-medium text-text-strong">
          <i className="fa-solid fa-folder-open mr-2 text-accent" aria-hidden />
          Pull clients from projects — check before adding
        </h3>
        <button
          type="button"
          data-testid="dns-pull-close"
          onClick={onClose}
          className="text-text-muted hover:text-text-strong"
          aria-label="Close pull preview"
        >
          <i className="fa-solid fa-xmark" />
        </button>
      </div>
      <div data-testid="dns-pull-preview" className="grid gap-3 md:grid-cols-3">
        <PreviewGroup tone="new" title="Will be added" count={freshCount} testId="dns-pull-new">
          {preview.fresh.map((c) => (
            <li key={c.clientId} data-testid="dns-pull-new-item" className="flex gap-2 py-0.5">
              <span className="truncate font-medium text-text-strong">{c.name}</span>
              {c.domain && c.domain !== c.name ? (
                <span className="truncate text-text-muted">{c.domain}</span>
              ) : null}
              {!c.domain ? <span className="text-text-muted italic">by name</span> : null}
            </li>
          ))}
        </PreviewGroup>
        <PreviewGroup
          tone="existing"
          title="Already in list"
          count={preview.alreadyListed.length}
          testId="dns-pull-existing"
        >
          {preview.alreadyListed.map((e) => (
            <li key={e.id} data-testid="dns-pull-existing-item" className="py-0.5">
              <span className="font-medium text-text-strong">{e.name}</span>
            </li>
          ))}
        </PreviewGroup>
        <PreviewGroup
          tone="bad"
          title="Removed — stays removed"
          count={preview.removed.length}
          testId="dns-pull-removed"
        >
          {preview.removed.map((e) => (
            <li key={e.id} data-testid="dns-pull-removed-item" className="py-0.5">
              <span className="font-medium text-text-strong">{e.name}</span>
            </li>
          ))}
        </PreviewGroup>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <button
          type="button"
          data-testid="dns-pull-cancel"
          onClick={onClose}
          className="text-sm text-text-muted hover:text-text-strong"
        >
          Cancel
        </button>
        {freshCount === 0 ? (
          <span data-testid="dns-pull-nothing" className="text-xs text-text-muted">
            Every project client is already covered — nothing to add.
          </span>
        ) : null}
        <button
          type="button"
          data-testid="dns-pull-confirm"
          disabled={freshCount === 0 || pullMut.isPending}
          onClick={handleConfirm}
          className="ml-auto rounded bg-primary px-4 py-2 text-sm font-medium text-surface hover:bg-primary-hover disabled:opacity-50"
        >
          {pullMut.isPending ? (
            <i className="fa-solid fa-spinner fa-spin mr-1" aria-hidden />
          ) : null}
          Add {freshCount}
        </button>
      </div>
      {error ? (
        <p
          data-testid="dns-pull-error"
          className="mt-3 rounded bg-danger/10 px-3 py-2 text-xs text-danger"
        >
          <i className="fa-solid fa-circle-exclamation mr-1" aria-hidden />
          {error}
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pull notice
// ---------------------------------------------------------------------------

function PullNotice({
  result,
  activeCount,
  removedCount,
  onDismiss,
}: {
  readonly result: DnsPullResult;
  /** Live tab counters — the notice quotes exactly what the tabs show. */
  readonly activeCount: number;
  readonly removedCount: number;
  readonly onDismiss: () => void;
}): React.JSX.Element {
  const details: string[] = [];
  if (result.alreadyListed > 0) details.push(`${result.alreadyListed} already in the list`);
  if (result.skippedRemoved > 0) {
    details.push(`${result.skippedRemoved} removed earlier — not brought back`);
  }
  return (
    <div
      data-testid="dns-pull-notice"
      role="status"
      className="mb-4 flex items-start gap-3 rounded-lg border border-accent/40 bg-accent/10 px-4 py-3 text-sm"
    >
      <i className="fa-solid fa-folder-open mt-0.5 text-accent-hover" aria-hidden />
      <div className="min-w-0">
        <p data-testid="dns-pull-added" className="font-medium text-text-strong">
          Added {result.added} {result.added === 1 ? 'client' : 'clients'} from projects
        </p>
        <p data-testid="dns-pull-totals" className="text-xs text-text-strong">
          List now: Active · {activeCount} · Removed · {removedCount}
        </p>
        {details.length > 0 ? (
          <p data-testid="dns-pull-details" className="text-xs text-text-muted">
            Project clients skipped: {details.join(' · ')}
          </p>
        ) : null}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="ml-auto text-text-muted hover:text-text-strong"
        aria-label="Dismiss"
      >
        <i className="fa-solid fa-xmark" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab
// ---------------------------------------------------------------------------

export interface DnsClientsTabProps {
  /** Admins edit; everyone else sees the list read-only. */
  readonly canEdit: boolean;
}

export function DnsClientsTab({ canEdit }: DnsClientsTabProps): React.JSX.Element {
  const query = useDnsClients();
  const toast = useToast();
  const removeMut = useRemoveDnsClient();
  const restoreMut = useRestoreDnsClient();
  const pullPreviewMut = usePreviewDnsPull();
  const [filter, setFilter] = useState<Filter>('active');
  const [search, setSearch] = useState('');
  const [pasteOpen, setPasteOpen] = useState(false);
  const [editing, setEditing] = useState<DnsClientEntry | null>(null);
  const [pullPreview, setPullPreview] = useState<DnsPullPreview | null>(null);
  const [pullResult, setPullResult] = useState<DnsPullResult | null>(null);
  // Rows created after the tab opened get a «new» tag — the admin sees where the paste landed.
  const [openedAt] = useState(() => new Date().toISOString());

  const data = query.data;
  const rows = useMemo(
    () =>
      data ? filterDnsEntries(data.entries, { active: filter === 'active', query: search }) : [],
    [data, filter, search],
  );

  function handleRemove(e: DnsClientEntry): void {
    removeMut.mutate(e.id, {
      onSuccess: () => toast.success(`${e.name} removed — find it under Removed`),
      onError: (err) => toast.error(errorMessage(err, 'Remove error')),
    });
  }
  function handleRestore(e: DnsClientEntry): void {
    restoreMut.mutate(e.id, {
      onSuccess: () => toast.success(`${e.name} is back in the list`),
      onError: (err) => toast.error(errorMessage(err, 'Restore error')),
    });
  }
  /** Step 1 of the pull: show what would be added — nothing is saved yet. */
  function handlePull(): void {
    setPullResult(null);
    pullPreviewMut.mutate(undefined, {
      onSuccess: (p) => setPullPreview(p),
      onError: (err) => toast.error(errorMessage(err, 'Pull preview failed')),
    });
  }

  if (query.isLoading) {
    return (
      <div data-testid="dns-loading" className="py-4 text-center text-gray-400">
        Loading…
      </div>
    );
  }
  if (query.isError || !data) {
    return (
      <div data-testid="dns-error" className="py-4 text-center text-red-500">
        {errorMessage(query.error, 'Failed to load the Do-Not-Source list')}
      </div>
    );
  }

  const searching = search.trim() !== '';

  return (
    <div data-testid="dns-tab" data-can-edit={canEdit ? 'true' : 'false'}>
      {/* Header */}
      <div className="mb-1 flex flex-wrap items-center gap-3">
        <h2 className="text-sm font-medium text-text-strong">Do-Not-Source</h2>
        <span
          data-testid="dns-active-count"
          className="rounded-full bg-primary px-2 py-0.5 text-xs font-semibold tabular-nums text-surface"
        >
          {data.activeCount} active
        </span>
        {canEdit ? (
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid="dns-pull"
              onClick={handlePull}
              disabled={pullPreviewMut.isPending || pullPreview !== null}
              className="rounded border border-border bg-white px-3 py-2 text-sm text-text-strong hover:border-primary disabled:opacity-50"
            >
              <i
                className={`fa-solid ${pullPreviewMut.isPending ? 'fa-spinner fa-spin' : 'fa-arrows-rotate'} mr-1`}
                aria-hidden
              />
              Pull clients from projects
            </button>
            <button
              type="button"
              data-testid="dns-add"
              onClick={() => setPasteOpen(true)}
              disabled={pasteOpen}
              className="rounded bg-primary px-3 py-2 text-sm text-surface hover:bg-primary-hover disabled:opacity-50"
            >
              <i className="fa-solid fa-plus mr-1" aria-hidden /> Add companies
            </button>
          </div>
        ) : (
          <span
            data-testid="dns-readonly"
            className="ml-auto inline-flex items-center gap-1 text-xs text-text-muted"
          >
            <i className="fa-solid fa-lock" aria-hidden /> Read-only — only an admin can change this
            list
          </span>
        )}
      </div>
      <p className="mb-4 text-xs text-gray-500">
        EER clients. Every new research run excludes these companies automatically and shows them as
        «EER client (global list)» under Excluded at search.
      </p>

      {canEdit && pullPreview ? (
        <PullPanel
          preview={pullPreview}
          onClose={() => setPullPreview(null)}
          onDone={(r) => {
            setPullPreview(null);
            setPullResult(r);
          }}
        />
      ) : null}
      {canEdit && pullResult ? (
        <PullNotice
          result={pullResult}
          activeCount={data.activeCount}
          removedCount={data.removedCount}
          onDismiss={() => setPullResult(null)}
        />
      ) : null}
      {canEdit && pasteOpen ? <PastePanel onClose={() => setPasteOpen(false)} /> : null}

      {/* Toolbar */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <SegmentedToggle<Filter>
          ariaLabel="Show entries"
          testId="dns-filter"
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'active', label: `Active · ${data.activeCount}` },
            { value: 'removed', label: `Removed · ${data.removedCount}` },
          ]}
        />
        <label className="relative ml-auto w-full max-w-xs">
          <i
            className="fa-solid fa-magnifying-glass pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-text-muted"
            aria-hidden
          />
          <input
            data-testid="dns-search"
            type="search"
            aria-label="Search by name or domain"
            placeholder="Search name or domain"
            className={`${FIELD_CLASS} pl-8`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
      </div>

      {/* Table / empty states */}
      {rows.length === 0 ? (
        <div
          data-testid="dns-empty"
          className="rounded-lg border border-dashed border-border px-4 py-10 text-center"
        >
          <i className="fa-solid fa-building-shield mb-2 text-2xl text-gray-300" aria-hidden />
          <p className="text-sm text-text-strong">
            {searching
              ? `No companies match «${search.trim()}»`
              : filter === 'removed'
                ? 'Nothing removed'
                : 'No companies in the list yet'}
          </p>
          {!searching && filter === 'active' && canEdit ? (
            <p className="mt-1 text-xs text-text-muted">
              Paste your client list or pull clients from projects.
            </p>
          ) : null}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table data-testid="dns-table" className="w-full border-collapse text-sm">
            <thead className="bg-surface-muted">
              <tr className="text-left text-xs font-medium tracking-wide text-gray-500">
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Domain</th>
                <th className="px-3 py-2">Source</th>
                <th className="px-3 py-2">Added</th>
                {canEdit ? <th className="w-24 px-3 py-2" aria-label="Actions" /> : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => {
                const isNew = e.createdAt > openedAt;
                return (
                  <tr
                    key={e.id}
                    data-testid="dns-row"
                    data-entry-id={e.id}
                    className={`group border-t border-border transition-colors hover:bg-surface-muted ${
                      isNew ? 'bg-success/5' : ''
                    } ${e.isActive ? '' : 'text-text-muted'}`}
                  >
                    <td className="px-3 py-2">
                      <span data-testid="dns-row-name" className="font-medium text-text-strong">
                        {e.name}
                      </span>
                      {isNew ? (
                        <span className="ml-2 rounded bg-success/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-success">
                          new
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2" data-testid="dns-row-domain">
                      {e.domain ? (
                        <span className="font-mono text-xs">{e.domain}</span>
                      ) : (
                        <span
                          className="text-xs italic text-text-muted"
                          title="Matched by company name"
                        >
                          — by name
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <SourceBadge entry={e} />
                    </td>
                    <td
                      className="whitespace-nowrap px-3 py-2 text-xs text-text-muted"
                      title={e.createdBy ?? 'Automatic'}
                    >
                      {formatDate(e.createdAt)}
                    </td>
                    {canEdit ? (
                      <td className="px-3 py-2 text-right">
                        {e.isActive ? (
                          <div className="flex justify-end gap-3 opacity-60 transition group-hover:opacity-100">
                            <button
                              type="button"
                              data-testid="dns-row-edit"
                              onClick={() => setEditing(e)}
                              className="text-xs text-gray-500 hover:text-primary"
                              aria-label={`Edit ${e.name}`}
                            >
                              <i className="fa-solid fa-pen" />
                            </button>
                            <button
                              type="button"
                              data-testid="dns-row-remove"
                              onClick={() => handleRemove(e)}
                              disabled={removeMut.isPending}
                              className="text-xs text-gray-500 hover:text-danger"
                              aria-label={`Remove ${e.name}`}
                            >
                              <i className="fa-solid fa-trash" />
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            data-testid="dns-row-restore"
                            onClick={() => handleRestore(e)}
                            disabled={restoreMut.isPending}
                            className="rounded border border-border px-2 py-1 text-xs text-text-strong hover:border-primary"
                          >
                            <i className="fa-solid fa-rotate-left mr-1" aria-hidden /> Restore
                          </button>
                        )}
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing !== null ? <EditDialog entry={editing} onClose={() => setEditing(null)} /> : null}
    </div>
  );
}

export default DnsClientsTab;
